import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  MapPinIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { MapPinIcon as MapPinSolid } from "@heroicons/react/24/solid";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import {
  createJournalEntry,
  deleteJournalEntry,
  fetchJournalEntries,
  updateJournalEntry,
} from "../../../api/journaling";

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

/**
 * Rich-text entry stream for a single journal: list of entries (newest /
 * pinned first) plus an inline composer that supports text + embedded images.
 */
export default function JournalEntries({ journalId }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const loadEntries = useMemo(
    () => async () => {
      if (!journalId) return;
      setIsLoading(true);
      const res = await fetchJournalEntries(journalId);
      setEntries(res?.data || []);
      setIsLoading(false);
    },
    [journalId]
  );

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const hasDraft = Boolean((draft || "").replace(/<(.|\n)*?>/g, "").trim());

  const handleCreate = async () => {
    if (!hasDraft) return;
    setIsSaving(true);
    const res = await createJournalEntry(journalId, { body_html: draft });
    setIsSaving(false);
    if (res?.errors) {
      toast.error(t("journal.entry_save_failed"));
      return;
    }
    setDraft("");
    setEntries((prev) => [res, ...prev]);
    toast.success(t("journal.entry_added"));
  };

  const handleTogglePin = async (entry) => {
    const res = await updateJournalEntry(journalId, entry.id, {
      pinned: !entry.pinned,
    });
    if (!res?.errors) loadEntries();
  };

  const handleSaveEdit = async (entry) => {
    const res = await updateJournalEntry(journalId, entry.id, {
      body_html: editingValue,
    });
    if (res?.errors) {
      toast.error(t("journal.entry_save_failed"));
      return;
    }
    setEditingId(null);
    setEditingValue("");
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? res : e)));
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(t("journal.entry_delete_confirm"))) return;
    const res = await deleteJournalEntry(journalId, entry.id);
    if (!res?.errors) {
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="mb-2 block text-sm font-medium text-slate-700">
          {t("journal.new_entry")}
        </label>
        <PromangRichTextEditor
          value={draft}
          onChange={setDraft}
          placeholder={t("journal.entry_placeholder")}
          size="md"
        />
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={!hasDraft || isSaving}
            onClick={handleCreate}
            className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
          >
            {isSaving ? t("Saving...") : t("journal.add_entry")}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-slate-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center text-sm text-slate-500">
          {t("journal.no_entries")}
        </div>
      ) : (
        <ol className="space-y-4">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className={`rounded-2xl border bg-white p-4 shadow-sm ${
                entry.pinned ? "border-amber-300" : "border-slate-200"
              }`}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">
                    {entry.author?.displayTitle || t("journal.unknown_author")}
                  </span>
                  <span className="mx-2">·</span>
                  {formatDateTime(entry.entry_at)}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    title={entry.pinned ? t("journal.unpin") : t("journal.pin")}
                    onClick={() => handleTogglePin(entry)}
                    className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-amber-600"
                  >
                    {entry.pinned ? (
                      <MapPinSolid className="h-4 w-4 text-amber-500" />
                    ) : (
                      <MapPinIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    title={t("Edit")}
                    onClick={() => {
                      setEditingId(entry.id);
                      setEditingValue(entry.body_html || "");
                    }}
                    className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title={t("Delete")}
                    onClick={() => handleDelete(entry)}
                    className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {editingId === entry.id ? (
                <div>
                  <PromangRichTextEditor
                    value={editingValue}
                    onChange={setEditingValue}
                    size="md"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(null);
                        setEditingValue("");
                      }}
                      className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(entry)}
                      className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      {t("Save")}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-slate-800"
                  dangerouslySetInnerHTML={{ __html: entry.body_html || "" }}
                />
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
